import type { FormEventHandler } from 'react'
import { useCallback, useEffect, useRef, useState } from 'react'
import { defineMessages, useIntl } from 'react-intl'
import { connect } from 'react-redux'
import { useAppDispatch } from 'hooks/reduxHooks'
import { formToObject } from 'lib/forms/formToObject'
import { syncUserDetails, updateAddPhonePromptedAt, updateUserDetails } from 'actions/AuthActions'
import { confirmGooglePhone } from 'api/auth'
import {
  consumeGooglePhoneCollectionAvailable,
  getGooglePhoneAccountId,
  getGooglePhoneCollectionAvailable,
  requestGooglePhone,
} from 'lib/auth/googlePhoneCollection'
import type { GooglePhoneFailure, GooglePhonePreview } from 'lib/auth/googlePhoneCollection'
import TextButton from 'LuxKit/Button/TextButton'
import CheckboxInput from 'LuxKit/Checkbox/CheckboxInput'
import PhoneInput from 'components/Common/Form/Input/PhoneInput'
import LegalText from './AccountAccessLegalText'
import Group from 'components/utils/Group'
import ModalBody from 'LuxKit/Modal/ModalBody'
import ModalContent from 'LuxKit/Modal/ModalContent'
import ModalHeader from 'LuxKit/Modal/ModalHeader'
import MessageBanner from 'LuxKit/Banners/MessageBanner'
import useModalElementContext from 'hooks/Modal/useModalElementContext'
import LineInfoCircleIcon from 'LuxKit/Icons/line/LineInfoCircleIcon'
import type { AccountAccessModalResult } from './AccountAccessModal.types'
import { trackClientEvent } from 'analytics/analytics'
import SolidGoogleIcon from 'LuxKit/Icons/solid/SolidGoogleIcon'
import { ChertoExperiments } from 'constants/experiments'
import useExperiment from 'hooks/Experiments/useExperiment'

interface PhoneNum {
  phone: string
  prefix: string
}

interface AddPhoneForm {
  phoneNum: PhoneNum
}

interface Props {
  error?: any
  regionCode?: string
  phone?: string
  email?: string
  memberId?: string
  dismissable?: boolean
  trackPromptImpression?: boolean
  hasDismissed?: boolean
  onDismissChange?: (dismissed: boolean) => void
}

const ADD_PHONE_OPTIMIZELY_EVENT = {
  id: '4586532951031808',
  key: 'add_phone_number',
} as const

const googlePhoneMessages = defineMessages({
  unavailable: {
    defaultMessage: 'Google did not share a verified mobile number. Please enter your number manually.',
    id: 'vj4oLs3mnh8Pytp4nXKK',
  },
  cancelled: {
    defaultMessage: 'Google phone sharing was cancelled. You can try again or enter your number manually.',
    id: 'hqLRNVsg3FsJFQfwIQ-h',
  },
  popup_blocked: {
    defaultMessage: 'The Google window could not open. Please allow popups for this site and try again.',
    id: 'Pk6VL0rnGyqcK62gsDgn',
  },
  account_mismatch: {
    defaultMessage: 'Please use the same Google account you used to sign up.',
    id: 'TmiSexQzzaUFxEK6VOSR',
  },
  failed: {
    defaultMessage: 'We could not get your phone number from Google. Please try again or enter it manually.',
    id: 'mHoIrR9MxknYwA6O_1VH',
  },
  confirmFailed: {
    defaultMessage: 'We could not save your phone number. Please try again.',
    id: 'tGLZ7NafvfAOZjSMtC0q',
  },
  collect: {
    defaultMessage: 'Use phone from Google',
    id: 'x2BOehp6JoC4kVPzWyM2',
  },
  confirm: {
    defaultMessage: 'Confirm Google phone number',
    id: 'jpsmH00XYN7G3YlTGes4',
  },
  edit: {
    defaultMessage: 'Edit phone number',
    id: 'QgOwLNZl3zyexD9Yao_t',
  },
})

function AccountAccessAddPhone(props: Props) {
  const {
    regionCode,
    phone,
    email,
    memberId,
    dismissable,
    trackPromptImpression = true,
    hasDismissed: hasDismissedProp,
    onDismissChange,
  } = props

  const intl = useIntl()
  const termsCheckboxRef = useRef<HTMLInputElement>(null)
  const phoneSaveSource = useRef<'manual' | 'google' | undefined>(undefined)
  const modalContext = useModalElementContext<AccountAccessModalResult>()
  const dispatch = useAppDispatch()
  const [googlePreview, setGooglePreview] = useState<GooglePhonePreview>()
  const [importedPhone, setImportedPhone] = useState<Pick<GooglePhonePreview, 'phone' | 'phonePrefix'>>()
  const [googlePhoneFailure, setGooglePhoneFailure] = useState<GooglePhoneFailure | 'confirmFailed'>()
  const [googlePhoneBusy, setGooglePhoneBusy] = useState(false)
  const [googleAccountId] = useState(getGooglePhoneAccountId)
  const [isGooglePhoneEligible] = useState(() => getGooglePhoneCollectionAvailable(memberId))
  useEffect(() => { consumeGooglePhoneCollectionAvailable() }, [])
  const { variation } = useExperiment(ChertoExperiments.croAddPhoneGoogle, {
    canActivate: isGooglePhoneEligible && !phone,
  })
  const canCollectGooglePhone = isGooglePhoneEligible && !phone && variation === 'variant'
  const [internalHasDismissed, setInternalHasDismissed] = useState(false)
  const isDismissedControlled = typeof hasDismissedProp === 'boolean'
  const hasDismissed = isDismissedControlled ? Boolean(hasDismissedProp) : internalHasDismissed

  const setHasDismissed = useCallback((nextState: boolean) => {
    if (!isDismissedControlled) {
      setInternalHasDismissed(nextState)
    }
    onDismissChange?.(nextState)
  }, [isDismissedControlled, onDismissChange])

  const onAddPhone = useCallback<FormEventHandler<HTMLFormElement>>((event) => {
    event.preventDefault()
    if (googlePreview || googlePhoneBusy) return
    const joinData = formToObject<AddPhoneForm>(event.currentTarget)
    phoneSaveSource.current = 'manual'
    dispatch(updateUserDetails(
      {
        phone: joinData.phoneNum.phone,
        phonePrefix: joinData.phoneNum.prefix,
      },
    ))
  }, [dispatch, googlePreview, googlePhoneBusy])

  const onConfirmGooglePhone = useCallback(async() => {
    if (!googlePreview || googlePhoneBusy || !termsCheckboxRef.current?.reportValidity()) return
    setGooglePhoneBusy(true)
    setGooglePhoneFailure(undefined)
    try {
      const account = await confirmGooglePhone(googlePreview.verificationToken)
      phoneSaveSource.current = 'google'
      dispatch(syncUserDetails({ phone: account.phone, phonePrefix: account.phonePrefix }))
    } catch {
      setGooglePhoneFailure('confirmFailed')
    } finally {
      setGooglePhoneBusy(false)
    }
  }, [dispatch, googlePreview, googlePhoneBusy])

  const onRequestGooglePhone = useCallback(async() => {
    if (googlePhoneBusy) return
    trackClientEvent({
      subject: 'add_phone_from_google_clicked',
      action: 'interaction',
      category: 'logging',
      type: 'operational',
    })
    setGooglePhoneFailure(undefined)
    setGooglePhoneBusy(true)
    const result = await requestGooglePhone(googleAccountId ?? email ?? '')
    setGooglePhoneBusy(false)
    if (result.outcome === 'preview' || result.outcome === 'unavailable') {
      trackClientEvent({
        subject: result.outcome === 'preview' ? 'add_phone_from_google_phone_found' : 'add_phone_from_google_no_phone_found',
        action: 'interaction',
        category: 'logging',
        type: 'operational',
      })
    }
    if (result.outcome === 'preview') {
      setGooglePreview(result.preview)
      setImportedPhone({ phone: result.preview.phone, phonePrefix: result.preview.phonePrefix })
    } else {
      setGooglePhoneFailure(result.outcome)
    }
  }, [email, googleAccountId, googlePhoneBusy])

  const onEditGooglePhone = useCallback(() => {
    setGooglePreview(undefined)
    setGooglePhoneFailure(undefined)
  }, [])

  const onClose = useCallback(() => {
    if (hasDismissed) {
      return modalContext.resolve({ loggedIn: true })
    }

    setHasDismissed(true)
  }, [modalContext, hasDismissed, setHasDismissed])

  useEffect(() => {
    if (trackPromptImpression) {
      dispatch(updateAddPhonePromptedAt(new Date()))
    }
    trackClientEvent({
      subject: 'add_phone_number',
      action: 'impression',
      category: 'logging',
      type: 'operational',
    })
  }, [dispatch, trackPromptImpression])

  useEffect(() => {
    if (phone?.length) {
      if (phoneSaveSource.current) {
        trackClientEvent({
          subject: 'phone_number_added',
          action: phoneSaveSource.current,
          category: 'logging',
          type: 'operational',
        })
        phoneSaveSource.current = undefined
      }
      const optimizelyEvent = {
        optimizelyEventId: ADD_PHONE_OPTIMIZELY_EVENT.id,
        optimizelyEventKey: ADD_PHONE_OPTIMIZELY_EVENT.key,
      }

      trackClientEvent({
        subject: 'add_phone_number',
        action: 'interaction',
        category: 'logging',
        type: 'operational',
        ...optimizelyEvent,
      })
      modalContext.resolve({ loggedIn: true })
    }
  }, [modalContext, phone])

  let title: string
  if (hasDismissed) {
    title = intl.formatMessage({
      id: 'VCyRccvh4C61jdcUdzqs',
      defaultMessage: 'Are you sure?',
    })
  } else {
    title = intl.formatMessage({
      id: 'wviA6ESdOgBP49KT65RG',
      defaultMessage: 'Secure your account with a mobile number',
    })
  }

  const subtitleText = intl.formatMessage({
    id: '06j1MVhGhMK9B3Yftmdi',
    defaultMessage: 'Link your mobile to your account for added security and a smoother, faster login experience.',
  })

  const warningText = intl.formatMessage({
    id: '4zdgDkj0smvGUStmyHH7',
    defaultMessage: 'Adding your phone number is highly recommended to help keep your account safe',
  })

  const phoneLabel = intl.formatMessage({
    id: 'T_4RhGB0-sNJgnkOJhK2',
    defaultMessage: 'Phone number',
  })

  const secureMyAccountLabel = intl.formatMessage({
    id: 'gOcKbwOUPcWnJ1cX6lj4',
    defaultMessage: 'Secure my account',
  })

  const addMobileLabel = intl.formatMessage({
    id: 'UuTr4xCfmW2QcmgjI2D2',
    defaultMessage: 'Add mobile number',
  })

  return (
    <>
      <ModalHeader
        title={title}
        subtitle={!hasDismissed ? subtitleText : undefined}
        dismissible={dismissable}
        onCloseButtonClick={onClose}
      />
      <ModalBody>
        <ModalContent>
          <form name="joinForm" onSubmit={onAddPhone}>
            <Group direction="vertical" gap={12}>
              {hasDismissed && <MessageBanner kind="warning" icon={<LineInfoCircleIcon />}>
                {warningText}
              </MessageBanner>}
              <Group direction="vertical" gap={4}>
                <PhoneInput
                  key={googlePreview ? 'google' : 'manual'}
                  id="phoneNum"
                  name="phoneNum"
                  label={phoneLabel}
                  prefixCountry={regionCode}
                  required
                  defaultPhoneNumber={importedPhone?.phone}
                  defaultPhonePrefix={importedPhone?.phonePrefix}
                  disabled={Boolean(googlePreview) || googlePhoneBusy}
                />
              </Group>
              {googlePhoneFailure && <MessageBanner kind="warning" icon={<LineInfoCircleIcon />}>
                {intl.formatMessage(googlePhoneMessages[googlePhoneFailure])}
              </MessageBanner>}
              <CheckboxInput
                ref={termsCheckboxRef}
                name="agreeTandC"
                data-testid="terms-and-privacy-agree-checkbox"
                required
              >
                <LegalText modalType="promptAddPhone" />
              </CheckboxInput>
              {!googlePreview && <TextButton
                kind="primary"
                fit="flex"
                type="submit"
                size="large"
                disabled={googlePhoneBusy}
              >
                {hasDismissed ? secureMyAccountLabel : addMobileLabel}
              </TextButton>}
              {!googlePreview && canCollectGooglePhone && <TextButton
                kind="secondary"
                fit="flex"
                type="button"
                size="large"
                startIcon={<SolidGoogleIcon />}
                onClick={onRequestGooglePhone}
                disabled={googlePhoneBusy}
              >
                {intl.formatMessage(googlePhoneMessages.collect)}
              </TextButton>}
              {googlePreview && <>
                <TextButton
                  kind="primary"
                  fit="flex"
                  type="button"
                  size="large"
                  startIcon={<SolidGoogleIcon />}
                  onClick={onConfirmGooglePhone}
                  disabled={googlePhoneBusy}
                >
                  {intl.formatMessage(googlePhoneMessages.confirm)}
                </TextButton>
                <TextButton
                  kind="secondary"
                  fit="flex"
                  type="button"
                  size="large"
                  onClick={onEditGooglePhone}
                  disabled={googlePhoneBusy}
                >
                  {intl.formatMessage(googlePhoneMessages.edit)}
                </TextButton>
              </>}
            </Group>
          </form>
        </ModalContent>
      </ModalBody>
    </>
  )
}

function mapStateToProps(state: App.State) {
  return {
    error: state.auth.error,
    regionCode: state.geo.currentRegionCode,
    phone: state.auth.account.phone,
    email: state.auth.account.email,
    memberId: state.auth.account.memberId,
  }
}

export default connect(mapStateToProps)(AccountAccessAddPhone)
